import React, { useState } from 'react';
import {
    View, Text, StyleSheet, TextInput, TouchableOpacity,
    Image, ScrollView, ActivityIndicator, Alert, Platform, Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { createPost } from '../../../api/social';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import EmojiPicker from 'rn-emoji-keyboard';
import ShortClipPicker from '../../../components/social/feed/ShortClipPicker';
import PhotoClipPicker from '../../../components/social/feed/PhotoClipPicker';
import PhotoClipPlayer from '../../../components/social/feed/PhotoClipPlayer';
import { Video } from 'expo-av';

const { width: SW } = Dimensions.get('window');
const CLIP_PREVIEW_W = SW * 0.35;
const CLIP_PREVIEW_H = CLIP_PREVIEW_W * (16 / 9);
const MAX_IMAGES = 5;

// ── Inline Progress Component ──────────────────────────────────────────────
const InlineProgress = ({ progress, size = 34, stroke = 3, color = '#a78bfa' }) => {
    const angle = progress * 360;
    return (
        <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
            <View style={{
                position: 'absolute', width: size, height: size, borderRadius: size / 2,
                borderWidth: stroke, borderColor: 'rgba(255,255,255,0.2)'
            }} />
            <View style={{
                position: 'absolute', width: size, height: size, borderRadius: size / 2,
                borderWidth: stroke, borderColor: color,
                borderLeftColor: 'transparent', borderBottomColor: 'transparent',
                transform: [{ rotate: `${angle}deg` }]
            }} />
            <Text style={{ color: '#fff', fontSize: 9, fontWeight: '800' }}>{Math.round(progress * 100)}%</Text>
        </View>
    );
};

const CreatePostScreen = ({ navigation, route }) => {
    const onPostCreated = route?.params?.onPostCreated;
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const insets = useSafeAreaInsets();

    const [content, setContent] = useState('');
    const [mediaList, setMediaList] = useState([]);   // images only
    const [clipItem, setClipItem] = useState(null);   // single video clip
    const [photoClipItem, setPhotoClipItem] = useState(null); // slideshow clip
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // overall post progress
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [showClipPicker, setShowClipPicker] = useState(false);
    const [showPhotoClipPicker, setShowPhotoClipPicker] = useState(false);

    // ── Pick photos from gallery ──────────────────────────────────────────────
    const pickImages = async () => {
        const remaining = MAX_IMAGES - mediaList.length;
        if (remaining <= 0) {
            Alert.alert('Image Limit Reached', `You can only attach up to ${MAX_IMAGES} images per post.`);
            return;
        }
        const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: false,
            quality: 0.85,
            allowsMultipleSelection: true,
            selectionLimit: remaining,
        });
        if (!result.canceled) {
            const incoming = result.assets;
            if (mediaList.length + incoming.length > MAX_IMAGES) {
                const allowed = incoming.slice(0, remaining);
                setMediaList(prev => [...prev, ...allowed]);
                Alert.alert('Some Images Skipped', `Only ${remaining} image${remaining > 1 ? 's were' : ' was'} added.`);
            } else {
                setMediaList(prev => [...prev, ...incoming]);
            }
        }
    };

    const removeImage = (index) => setMediaList(prev => prev.filter((_, i) => i !== index));
    const removeClip = () => setClipItem(null);
    const removePhotoClip = () => setPhotoClipItem(null);

    // ── Submit post ───────────────────────────────────────────────────────────
    const handlePost = async () => {
        if (!content.trim() && mediaList.length === 0 && !clipItem && !photoClipItem) {
            Alert.alert('Empty Post', 'Please add some text, photos, or a clip.');
            return;
        }
        setLoading(true);
        setUploadProgress(0.05);
        try {
            // Simulated realistic progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 0.98) { clearInterval(progressInterval); return 0.98; }
                    // Slow down as it reaches the end to wait for actual response
                    const step = prev < 0.7 ? 0.04 : 0.01;
                    return prev + step;
                });
            }, 200);

            let postDataPayload;

            if (mediaList.length > 0 || clipItem || photoClipItem) {
                const formData = new FormData();
                formData.append('content', content);
                formData.append('visibility', 'public');

                // Determine post type
                if (photoClipItem) {
                    formData.append('type', 'photo_clip');
                    formData.append('animationConfig', JSON.stringify(photoClipItem.animationConfig));
                } else if (clipItem && mediaList.length > 0) {
                    formData.append('type', 'mixed');
                } else if (clipItem) {
                    formData.append('type', 'video');
                } else {
                    formData.append('type', 'photo');
                }

                // Append images
                if (!photoClipItem) {
                    for (let i = 0; i < mediaList.length; i++) {
                        const media = mediaList[i];
                        if (Platform.OS === 'web') {
                            const response = await fetch(media.uri);
                            const blob = await response.blob();
                            formData.append('media', blob, `photo_${Date.now()}_${i}.jpg`);
                        } else {
                            const uri = media.uri;
                            const ext = uri.split('.').pop().toLowerCase();
                            formData.append('media', {
                                uri: Platform.OS === 'android' ? uri : uri.replace('file://', ''),
                                name: `photo_${Date.now()}_${i}.${ext}`,
                                type: `image/${ext === 'png' ? 'png' : 'jpeg'}`,
                            });
                        }
                    }
                }

                // Append images for photo clip
                if (photoClipItem) {
                    for (let i = 0; i < photoClipItem.images.length; i++) {
                        const uri = photoClipItem.images[i];
                        if (Platform.OS === 'web') {
                            const response = await fetch(uri);
                            const blob = await response.blob();
                            formData.append('media', blob, `slideshow_${Date.now()}_${i}.jpg`);
                        } else {
                            const ext = uri.split('.').pop().toLowerCase();
                            formData.append('media', {
                                uri: Platform.OS === 'android' ? uri : uri.replace('file://', ''),
                                name: `slideshow_${Date.now()}_${i}.${ext}`,
                                type: `image/${ext === 'png' ? 'png' : 'jpeg'}`,
                            });
                        }
                    }
                }

                // Append clip
                if (clipItem) {
                    if (Platform.OS === 'web') {
                        const response = await fetch(clipItem.uri);
                        const blob = await response.blob();
                        formData.append('media', blob, clipItem.fileName || `clip_${Date.now()}.mp4`);
                    } else {
                        const uri = clipItem.uri;
                        const ext = uri.split('.').pop().toLowerCase();
                        formData.append('media', {
                            uri: Platform.OS === 'android' ? uri : uri.replace('file://', ''),
                            name: clipItem.fileName || `clip_${Date.now()}.mp4`,
                            type: `video/${ext === 'mov' ? 'quicktime' : 'mp4'}`,
                        });
                    }
                }
                postDataPayload = formData;
            } else {
                postDataPayload = { content, visibility: 'public' };
            }

            const response = await createPost(postDataPayload);
            clearInterval(progressInterval);
            setUploadProgress(1);
            await new Promise(resolve => setTimeout(resolve, 500));
            queryClient.invalidateQueries({ queryKey: ['feed'] });
            if (onPostCreated) onPostCreated(response);
            navigation.goBack();
        } catch (error) {
            console.error('Post creation error:', error);
            Alert.alert('Post Error', 'Failed to create post. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const canPost = !loading && (!!content.trim() || mediaList.length > 0 || !!clipItem || !!photoClipItem);

    return (
        <View style={styles.container}>
            <LinearGradient
                colors={['#0891b2', '#0369a1']}
                start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                style={[styles.fixedHeader, { paddingTop: insets.top + 15 }]}
            >
                <View style={styles.headerContent}>
                    <TouchableOpacity style={styles.cancelBtn} onPress={() => navigation.goBack()} disabled={loading}>
                        <Ionicons name="close" size={22} color="#FFFFFF" />
                    </TouchableOpacity>
                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle}>New Post</Text>
                    </View>
                    <TouchableOpacity
                        style={[styles.postBtn, !canPost && styles.postBtnDisabled, loading && styles.postBtnActive]}
                        onPress={handlePost}
                        disabled={!canPost}
                    >
                        {loading ? (
                            <View style={styles.btnProgressRow}>
                                <ActivityIndicator size="small" color="#FFFFFF" style={{ marginRight: 6 }} />
                                <Text style={styles.postBtnText}>{Math.round(uploadProgress * 100)}%</Text>
                            </View>
                        ) : (
                            <Text style={styles.postBtnText}>Post</Text>
                        )}
                    </TouchableOpacity>
                </View>
            </LinearGradient>

            <ScrollView style={styles.scrollArea} keyboardShouldPersistTaps="handled">
                <View style={styles.userRow}>
                    <UserAvatar source={user?.profileImageUrl} name={user?.name} size={44} />
                    <View style={styles.userInfo}>
                        <Text style={styles.userName}>{user?.name}</Text>
                        <TouchableOpacity style={styles.visibilityChip}>
                            <Ionicons name="earth" size={12} color="#0891b2" />
                            <Text style={styles.visibilityText}>Public</Text>
                            <Ionicons name="chevron-down" size={10} color="#0891b2" />
                        </TouchableOpacity>
                    </View>
                </View>

                <TextInput
                    style={styles.input}
                    placeholder="What's on your mind?"
                    placeholderTextColor="#9CA3AF"
                    multiline
                    autoFocus
                    value={content}
                    onChangeText={setContent}
                />

                {(mediaList.length > 0 || clipItem || photoClipItem) && (
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.mediaScroll}
                        contentContainerStyle={styles.mediaScrollContent}
                    >
                        {mediaList.map((item, index) => (
                            <View key={`img-${index}`} style={styles.imagePreviewWrap}>
                                <Image source={{ uri: item.uri }} style={styles.imagePreview} resizeMode="cover" />
                                <TouchableOpacity style={styles.removeBtn} onPress={() => removeImage(index)}>
                                    <Ionicons name="close-circle" size={22} color="#FFFFFF" />
                                </TouchableOpacity>
                            </View>
                        ))}
                        {clipItem && (
                            <View style={[styles.clipPreviewWrap, { width: CLIP_PREVIEW_W, height: CLIP_PREVIEW_H }]}>
                                <Video
                                    source={{ uri: clipItem.uri }}
                                    style={styles.clipPreviewVideo}
                                    resizeMode="cover"
                                    shouldPlay={false}
                                    isMuted
                                />
                                <LinearGradient colors={['transparent', 'rgba(0,0,0,0.7)']} style={styles.clipGradOverlay} />
                                <View style={styles.clipPlayIcon}>
                                    <Ionicons name="play-circle" size={28} color="rgba(255,255,255,0.9)" />
                                </View>
                                {clipItem.duration && (
                                    <View style={styles.clipDurationBadge}>
                                        <Ionicons name="timer-outline" size={9} color="#fff" />
                                        <Text style={styles.clipDurationText}>
                                            {clipItem.duration > 30 ? '↓30s' : `${Math.floor(clipItem.duration)}s`}
                                        </Text>
                                    </View>
                                )}
                                <View style={styles.clipLabel}>
                                    <Ionicons name="film" size={10} color="#a78bfa" />
                                    <Text style={styles.clipLabelText}>CLIP</Text>
                                </View>
                                <TouchableOpacity style={styles.removeBtn} onPress={removeClip}>
                                    <Ionicons name="close-circle" size={22} color="#FFFFFF" />
                                </TouchableOpacity>
                                {loading && (
                                    <View style={styles.clipProgressOverlay}>
                                        <InlineProgress progress={uploadProgress} />
                                    </View>
                                )}
                            </View>
                        )}
                        {photoClipItem && (
                            <View style={[styles.clipPreviewWrap, { width: CLIP_PREVIEW_W, height: CLIP_PREVIEW_H }]}>
                                <PhotoClipPlayer
                                    images={photoClipItem.images}
                                    animationConfig={photoClipItem.animationConfig}
                                    width={CLIP_PREVIEW_W}
                                    height={CLIP_PREVIEW_H}
                                    borderRadius={16}
                                />
                                <View style={[styles.clipLabel, { backgroundColor: 'rgba(6,182,212,0.6)' }]}>
                                    <Ionicons name="sparkles" size={10} color="#fff" />
                                    <Text style={styles.clipLabelText}>SLIDESHOW</Text>
                                </View>
                                <TouchableOpacity style={styles.removeBtn} onPress={removePhotoClip}>
                                    <Ionicons name="close-circle" size={22} color="#FFFFFF" />
                                </TouchableOpacity>
                                {loading && (
                                    <View style={styles.clipProgressOverlay}>
                                        <InlineProgress progress={uploadProgress} />
                                    </View>
                                )}
                            </View>
                        )}
                    </ScrollView>
                )}
            </ScrollView>

            <View style={[styles.footer, { paddingBottom: Platform.OS === 'ios' ? insets.bottom + 8 : 16 }]}>
                <Text style={styles.footerLabel}>Add to your post</Text>
                <View style={styles.footerActions}>
                    <TouchableOpacity style={styles.toolItem} onPress={pickImages} disabled={mediaList.length >= MAX_IMAGES}>
                        <View style={[styles.toolIconBg, { backgroundColor: 'rgba(6,182,212,0.1)' }, mediaList.length >= MAX_IMAGES && { opacity: 0.4 }]}>
                            <Ionicons name="image-outline" size={22} color="#0891b2" />
                        </View>
                        <Text style={styles.toolLabel}>Photo</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.toolItem} onPress={() => setShowPhotoClipPicker(true)}>
                        <View style={[styles.toolIconBg, { backgroundColor: 'rgba(6,182,212,0.1)' }]}>
                            <Ionicons name={photoClipItem ? "sparkles" : "sparkles-outline"} size={22} color="#0891b2" />
                        </View>
                        <Text style={styles.toolLabel}>Slideshow</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.toolItem} onPress={() => setShowClipPicker(true)}>
                        <View style={[styles.toolIconBg, { backgroundColor: 'rgba(124,58,237,0.1)' }]}>
                            <Ionicons name={clipItem ? "checkmark-circle" : "film-outline"} size={22} color="#7C3AED" />
                        </View>
                        <Text style={styles.toolLabel}>Clip</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.toolItem} onPress={() => setIsEmojiPickerOpen(true)}>
                        <View style={[styles.toolIconBg, { backgroundColor: 'rgba(236,72,153,0.1)' }]}>
                            <Ionicons name="happy-outline" size={22} color="#EC4899" />
                        </View>
                        <Text style={styles.toolLabel}>Emoji</Text>
                    </TouchableOpacity>
                </View>
            </View>

            <EmojiPicker
                onEmojiSelected={(emojiObject) => setContent(prev => prev + emojiObject.emoji)}
                open={isEmojiPickerOpen}
                onClose={() => setIsEmojiPickerOpen(false)}
                enableSearchBar
                enableRecentlyUsed
            />
            <ShortClipPicker
                visible={showClipPicker}
                onClose={() => setShowClipPicker(false)}
                onClipSelected={(clip) => { setClipItem(clip); setShowClipPicker(false); }}
            />
            <PhotoClipPicker
                visible={showPhotoClipPicker}
                onClose={() => setShowPhotoClipPicker(false)}
                onClipSelected={(clip) => { setPhotoClipItem(clip); setShowPhotoClipPicker(false); }}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF' },
    fixedHeader: { height: Platform.OS === 'ios' ? 105 : 95, justifyContent: 'center' },
    headerContent: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingBottom: 8, gap: 10 },
    cancelBtn: { width: 38, height: 38, borderRadius: 11, backgroundColor: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center' },
    headerCenter: { flex: 1 },
    headerTitle: { fontSize: 20, fontWeight: '800', color: '#FFFFFF', letterSpacing: 0.3 },
    postBtn: { backgroundColor: 'rgba(255,255,255,0.2)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.35)', paddingHorizontal: 20, paddingVertical: 8, borderRadius: 20, minWidth: 68, alignItems: 'center' },
    postBtnDisabled: { opacity: 0.4 },
    postBtnText: { color: '#FFFFFF', fontWeight: '700', fontSize: 14 },
    scrollArea: { flex: 1, paddingHorizontal: 16, paddingTop: 14 },
    userRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
    userInfo: { marginLeft: 12 },
    userName: { fontWeight: '700', fontSize: 15, color: '#111827', marginBottom: 4 },
    visibilityChip: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(8,145,178,0.3)', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 2, gap: 3, alignSelf: 'flex-start', backgroundColor: 'rgba(6,182,212,0.06)' },
    visibilityText: { fontSize: 12, color: '#0891b2', fontWeight: '600' },
    input: { fontSize: 18, color: '#111827', minHeight: 120, textAlignVertical: 'top', lineHeight: 26 },
    mediaScroll: { marginTop: 14, marginBottom: 10 },
    mediaScrollContent: { paddingRight: 16, gap: 10 },
    imagePreviewWrap: { width: 110, height: 110, borderRadius: 14, overflow: 'hidden', position: 'relative' },
    imagePreview: { width: '100%', height: '100%' },
    clipPreviewWrap: { borderRadius: 16, overflow: 'hidden', position: 'relative', backgroundColor: '#0d0020', borderWidth: 1.5, borderColor: 'rgba(124,58,237,0.4)' },
    clipPreviewVideo: { ...StyleSheet.absoluteFillObject },
    clipGradOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 60 },
    clipPlayIcon: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 30, alignItems: 'center', justifyContent: 'center' },
    clipDurationBadge: { position: 'absolute', bottom: 22, left: 6, flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: 'rgba(0,0,0,0.65)', borderRadius: 6, paddingHorizontal: 5, paddingVertical: 2 },
    clipDurationText: { color: '#fff', fontSize: 9, fontWeight: '800' },
    clipLabel: { position: 'absolute', bottom: 6, left: 6, flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: 'rgba(124,58,237,0.5)', borderRadius: 6, paddingHorizontal: 5, paddingVertical: 2 },
    clipLabelText: { color: '#e9d5ff', fontSize: 8, fontWeight: '900', letterSpacing: 0.5 },
    removeBtn: { position: 'absolute', top: 6, right: 6, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 12 },
    footer: { borderTopWidth: 1, borderTopColor: 'rgba(6,182,212,0.12)', paddingHorizontal: 16, paddingTop: 12, backgroundColor: '#FFFFFF' },
    footerLabel: { fontSize: 11, fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 },
    footerActions: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 4 },
    toolItem: { alignItems: 'center', gap: 6 },
    toolIconBg: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center' },
    toolLabel: { fontSize: 11, color: '#6B7280', fontWeight: '500' },
    uploadOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
    uploadCard: { width: SW * 0.7, backgroundColor: '#fff', borderRadius: 24, padding: 24, alignItems: 'center', elevation: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 10 },
    uploadTitle: { fontSize: 16, fontWeight: '800', color: '#1f2937', marginTop: 16, marginBottom: 8 },
    progressTrack: { width: '100%', height: 6, backgroundColor: '#f3f4f6', borderRadius: 3, overflow: 'hidden', marginBottom: 6 },
    progressBar: { height: '100%', backgroundColor: '#a78bfa', borderRadius: 3 },
    uploadSub: { fontSize: 12, fontWeight: '600', color: '#6b7280' },
    
    // New progress styles
    postBtnActive: { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)' },
    btnProgressRow: { flexDirection: 'row', alignItems: 'center' },
    clipProgressOverlay: { 
        position: 'absolute', top: 6, right: 38, 
        backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 20, padding: 4 
    },
});

export default CreatePostScreen;
